import {DataStatus, Form, FormSubmitHandler} from 'platform/components';
import {Box} from 'platform/foundation';

import {
  useGetMandatoryFieldsDefinitionQuery,
  useGetStageSequenceMandatoryFieldsListQuery,
  useSetStageSequenceMandatoryFieldsMutation,
} from '@dms/api/settings';
import i18n from '@dms/i18n';
import {settingsRoutes, testIds} from '@dms/routes';
import {SettingsFooter} from '@dms/settings/shared';
import {handleApiError, usePermissions} from '@dms/shared';

import {ApiError, useNavigate} from 'shared/web';

import {
  mandatoryFieldsFormSchema,
  MandatoryFieldsRequestFormSchemaType,
} from '../form/MandatoryFieldsFormSchema';
import {MandatoryFieldsGroup} from './MandatoryFieldsGroup';

interface MandatoryFieldsSectionProps {
  id: string;
  name: string;
}

export function MandatoryFieldsSection(props: MandatoryFieldsSectionProps) {
  const navigate = useNavigate();
  const [setMandatoryFields] = useSetStageSequenceMandatoryFieldsMutation();

  const [canEditMandatoryFields] = usePermissions({
    permissionKeys: ['editMandatoryFields'],
  });

  const {
    data: mandatoryFieldsDefinition,
    isLoading,
    isError,
  } = useGetMandatoryFieldsDefinitionQuery({
    definitionId: props.id,
  });

  const {data: mandatorySS, isLoading: isMandatorySSLoading} =
    useGetStageSequenceMandatoryFieldsListQuery({
      definitionId: props.id,
    });

  const handleSubmit: FormSubmitHandler<MandatoryFieldsRequestFormSchemaType> = (
    formData,
    _setErrors,
    reset,
    blockerContext
  ) => {
    const promises: Promise<void>[] = [];

    mandatoryFieldsDefinition?.stageSequences.forEach((stageSequence) => {
      const mandatoryFields = Object.entries(formData.fields).flatMap(([fieldId, field]) => {
        const stageEntries = Object.entries(field.stages ?? {})
          .filter(([stageId]) => stageId === stageSequence.id)
          .map(([_stageId, stageDefinition]) => ({
            fieldId,
            stageId: String(
              Array.isArray(stageDefinition)
                ? (stageDefinition?.[0] ?? '')
                : (stageDefinition ?? '')
            ),
          }))
          .filter((mandatoryField) => mandatoryField.stageId !== '');

        return stageEntries;
      });

      promises.push(
        setMandatoryFields({
          definitionId: props.id,
          stageSequenceId: stageSequence.id,
          stageSequenceMandatoryFieldsRequestBody: {
            mandatoryFields,
          },
        }).unwrap()
      );
    });

    return Promise.all(promises)
      .then(() => {
        const updatedValues = {...formData};

        Object.entries(formData.fields).forEach(([fieldId, field]) => {
          if (field.isEnabled) {
            if (Object.values(field.stages ?? {}).every((stage) => stage?.length === 0)) {
              updatedValues.fields[fieldId].isEnabled = false;
            }
          }
        });

        reset(updatedValues, {keepValues: false});
      })
      .catch((e: ApiError) =>
        handleApiError(e, {signalApiError: blockerContext?.reportApiFailure})
      );
  };

  const getInitialFormValues = (): Partial<MandatoryFieldsRequestFormSchemaType> => {
    if (!mandatoryFieldsDefinition || !mandatorySS) {
      return {};
    }

    const initialValues: MandatoryFieldsRequestFormSchemaType = {fields: {}};

    mandatorySS.stageSequences.forEach((seq) => {
      seq.mandatoryFields.forEach((mandatoryField) => {
        if (!initialValues.fields[mandatoryField.fieldId]) {
          initialValues.fields[mandatoryField.fieldId] = {
            stages: {},
            isEnabled: true,
          };
        }

        initialValues.fields[mandatoryField.fieldId].stages![seq.stageSequenceId] =
          mandatoryField.stageId;
      });
    });

    return initialValues;
  };

  const defaultValues: Partial<MandatoryFieldsRequestFormSchemaType> = getInitialFormValues();

  return (
    <DataStatus isLoading={isLoading || isMandatorySSLoading} isError={isError}>
      <Form<MandatoryFieldsRequestFormSchemaType>
        onSubmit={handleSubmit}
        experimentalZodSchema={mandatoryFieldsFormSchema}
        shouldWatchForUnsavedChanges
        defaultValues={defaultValues}
      >
        {(control) => (
          <>
            <Box paddingBottom={20}>
              {mandatoryFieldsDefinition?.fieldGroups?.map((fieldGroup, index, array) => (
                <MandatoryFieldsGroup
                  key={fieldGroup.id}
                  fieldGroup={fieldGroup}
                  isLast={index === array.length - 1}
                  stageSequences={mandatoryFieldsDefinition?.stageSequences}
                  defaultValues={defaultValues}
                  control={control}
                />
              ))}
            </Box>

            <SettingsFooter
              actions={[
                {
                  type: 'button',
                  title: i18n.t('general.actions.discardChanges'),
                  variant: 'secondary',
                  isDisabled: !canEditMandatoryFields,
                  onClick: () => navigate(settingsRoutes.mandatoryFields),
                },
                {
                  type: 'form-button',
                  control,
                  buttonType: 'submit',
                  isDisabled: !canEditMandatoryFields,
                  title: i18n.t('general.actions.saveChanges'),
                },
              ]}
              data-testid={testIds.settings.mandatoryFields('footer')}
            />
          </>
        )}
      </Form>
    </DataStatus>
  );
}
